import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlineChevronUp16 } from "assets/svg/outline-chevron-up-16.svg";
import IconButton from "components/icon-button";
import TimezoneDropdown from "components/timezone-dropdown/TimezoneDropdown";
import moment from "moment-timezone";
import { Button } from "react-bootstrap";
import { useMemo } from "react";
import getClasses from "utils/getClasses";
import FullDayEventTimeslot from "../full-day-event-timeslot/FullDayEventTimeslot";
import TimeslotTitle from "../timeslot-title/TimeslotTitle";
import styles from "./ScrollableCalendarViewHeader.module.scss";
import { FullDayEventSlotItem } from "./ScrollableCalendarViewHeader.types";
import useScrollableCalendarViewHeader from "./useScrollableCalendarViewHeader";
import { useCalendarAppointments } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarAppointmentsContext";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { useSessionTypes } from "contexts/SessionTypesContext";

type Props = {
  datesForWeek: {
    [x: string]: Date;
  };
  today: Date;
};

const FullDayEventSlotContent = ({
  item,
  onExpandClick,
}: {
  item: FullDayEventSlotItem;
  onExpandClick: () => void;
}) => {
  switch (item.type) {
    case "EMPTY": {
      return <div className={styles["th-body--events"]}></div>;
    }
    case "EVENT_START": {
      const { event, eventType } = item;
      return (
        <div className={styles["th-body--events"]}>
          <div className={styles["event-container"]}>
            <FullDayEventTimeslot event={event} eventType={eventType} />
          </div>
        </div>
      );
    }
    case "EXPAND_PROMPT": {
      const label = `${item.moreEvents} more`;
      return (
        <div className={styles["th-body--events"]}>
          <div className={styles["event-container"]}>
            <Button
              tabIndex={0}
              variant={"light"}
              className={styles["more"]}
              title={label}
              onClick={onExpandClick}
            >
              <TimeslotTitle>{label}</TimeslotTitle>
            </Button>
          </div>
        </div>
      );
    }
    case "EVENT_CONTINUED":
    default: {
      return null;
    }
  }
};

const DayLabel = ({
  dayOfWeek,
  dayOfMonth,
  isToday,
  isInThePast,
  isWeeklyView,
  dayWidthPercentage,
  onDayClick,
}: {
  dayOfWeek: string;
  dayOfMonth: string;
  isToday: boolean;
  isInThePast: boolean;
  isWeeklyView: boolean;
  dayWidthPercentage: number;
  onDayClick: () => void;
}) => (
  <div
    className={styles["day-label-container"]}
    style={{ width: `${dayWidthPercentage}%` }}
  >
    <div className={isInThePast ? styles["day-of-week-label--past"] : ""}>
      {dayOfWeek}
    </div>
    <div
      className={getClasses(
        styles["day-of-month-label"],
        isToday && styles["day-of-month-label--today"],
        isInThePast && styles["day-of-month-label--past"],
        isWeeklyView && styles["day-of-month-label--week"],
      )}
      onClick={onDayClick}
    >
      {dayOfMonth}
    </div>
  </div>
);

const ScrollableCalendarViewHeader0 = ({ datesForWeek, today }: Props) => {
  const {
    timezone,
    setTimezone,
    expanded,
    setExpanded,
    showExpandToggle,
    fullDayEventSlots,
    isWeeklyView,
    handleDayLabelClick,
  } = useScrollableCalendarViewHeader(datesForWeek);

  const { visibleTeamMemberIds } = useCalendarAppointments();
  const { teamMembers } = useTeamContext();
  const { setProviderId } = useSessionTypes();

  const dateCalculations = useMemo(() => {
    return Object.entries(datesForWeek).map(([dayOfWeek, date]) => {
      const dateMoment = moment(date).tz(timezone);
      const dayOfMonth = dateMoment.format("D");
      const isToday = dateMoment.isSame(today, "day");
      const isInThePast = dateMoment.isBefore(today, "day");

      return {
        dayOfWeek,
        date,
        dayOfMonth,
        isToday,
        isInThePast,
      };
    });
  }, [datesForWeek, timezone, today]);

  const columnSpan = useMemo(() => {
    return (
      Object.keys(datesForWeek).length *
      (visibleTeamMemberIds.length > 0 ? visibleTeamMemberIds.length : 1)
    );
  }, [datesForWeek, visibleTeamMemberIds]);

  const dayWidthPercentage = useMemo(() => {
    return 100 / Object.keys(datesForWeek).length;
  }, [datesForWeek]);

  const teamMemberWidthPercentage = useMemo(() => {
    return visibleTeamMemberIds.length > 0
      ? 100 / visibleTeamMemberIds.length
      : 0;
  }, [visibleTeamMemberIds]);

  return (
    <tr className={styles["header-row"]}>
      <th className={styles["timezone-dropdown-cell"]}>
        <div className={styles["timezone__content"]}>
          <TimezoneDropdown
            value={timezone}
            onApply={setTimezone}
            className={styles["timezone__dropdown"]}
          />
          {showExpandToggle && (
            <IconButton
              className={styles["expand-toggle"]}
              onClick={() => setExpanded((s) => !s)}
            >
              {expanded ? <OutlineChevronUp16 /> : <OutlineChevronDown16 />}
            </IconButton>
          )}
        </div>
      </th>

      <th className={styles["header-content-container"]} colSpan={columnSpan}>
        <div className={styles["header-content"]}>
          <div className={"d-flex"}>
            {dateCalculations.map(
              ({ dayOfWeek, date, dayOfMonth, isToday, isInThePast }) => (
                <DayLabel
                  key={dayOfWeek}
                  dayOfWeek={dayOfWeek}
                  dayOfMonth={dayOfMonth}
                  isToday={isToday}
                  isInThePast={isInThePast}
                  isWeeklyView={isWeeklyView}
                  dayWidthPercentage={dayWidthPercentage}
                  onDayClick={() => handleDayLabelClick(date)}
                />
              ),
            )}
          </div>

          {fullDayEventSlots.map((row, i) => (
            <div key={i} className={styles["full-day-events-row"]}>
              {row.map((item, j) => (
                <div
                  key={`${i}-${j}`}
                  style={{
                    width: `${100 / row.length}%`,
                    display: "inline-block",
                    borderRight: "1px solid #E0E0E0",
                  }}
                >
                  <FullDayEventSlotContent
                    item={item}
                    onExpandClick={() => setExpanded(true)}
                  />
                </div>
              ))}
            </div>
          ))}

          <div className={styles["team-members-row"]}>
            {dateCalculations.map(({ dayOfWeek }) => (
              <div
                key={dayOfWeek}
                style={{
                  width: `${dayWidthPercentage}%`,
                  display: "inline-block",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                {visibleTeamMemberIds.map((teamMemberId) => {
                  const teamMember = teamMembers.find(
                    (member) => member.orchid_user_id === teamMemberId,
                  );
                  if (!teamMember) return null;

                  return (
                    <div
                      key={`${dayOfWeek}-${teamMemberId}`}
                      style={{
                        width: `${teamMemberWidthPercentage}%`,
                        display: "inline-block",
                        textAlign: "center",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        padding: "0 4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setProviderId(teamMemberId)}
                      title={`Click to set ${teamMember.first_name} ${teamMember.last_name} as the selected provider`}
                    >
                      {teamMember.first_name} {teamMember.last_name}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </th>
    </tr>
  );
};

export default ScrollableCalendarViewHeader0;
